import AirDatepicker from 'air-datepicker';
import localeEn from 'air-datepicker/locale/en';
import { initCalendar } from './calendar.js';
import { initDropdowns, closeAllDropdowns } from './dropdown.js';
import { updateRatesView, initDynamicPrices, syncCouponVisibility } from './prices.js';
import { initPatientBlocks, syncPatientDependentBlocks } from './patientBlocks.js';
//import { initFakeUploader, initVeteranUploadToggle } from './uploader.js';
import { bindBookButtonsValidation, bindClientFormValidation } from './validation.js';
import { initWorkflow } from './workflow.js';
import { initPaymentForm } from './payment.js';
import { qs } from './utils.js';
import { state } from './state.js';
import { CFG } from './config.js';
import { buildPayload } from './payload.js';
import { saveAppointmentOptions, showAlert, showLoading, hideLoading, getCsrf, setCsrfFromXhr } from './api.js';

const cleanupCouponModalState = () => {
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.documentElement.style.removeProperty('overflow');

    document.querySelectorAll('.modal-backdrop').forEach((backdrop) => {
        backdrop.remove();
    });
};

/**
 * Hide the "coupon applied" toast and clean up any stray modal state.
 * Called whenever the coupon becomes inapplicable — e.g. the patient type
 * or veteran flag changed.
 */
export function hideCouponToast () {
    const couponToast = document.getElementById('couponToast');
    if (!couponToast) return;
    couponToast.classList.remove('is-visible');
    cleanupCouponModalState();
}

export default function initBook (containerSelector, {
    buttonSelector = '.book-now-btn',
    errorClass = CFG.classes.error,
    patientDropdownId = CFG.ids.patientDd,
    returningValue = CFG.values.returning,
    newValue = CFG.values.newcomer,
    returnBlockSelector = '.book__optionReturn',
    newBlockSelector = '.book__optionNew',
    hiddenClass = CFG.classes.hidden,
    calendarSelector = CFG.selectors.calendar,
    datepickerOptions = {}
} = {}) {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    console.log('Initializing booking component in', containerSelector);

    // migrated to workflow.js
    initCalendar(container, { calendarSelector, AirDatepicker, localeEn, datepickerOptions });

    const { returnInput } = initPatientBlocks(container, { returnBlockSelector, newBlockSelector, hiddenClass, errorClass });

    const dropdowns = initDropdowns(container, {
        patientDropdownId,
        errorClass,
        onChange: (dropdownEl) => {
            if (dropdownEl.matches(patientDropdownId)) {
                state.patientType = dropdownEl.dataset.value || null;
                syncPatientDependentBlocks(container, { patientDropdownId, returningValue, newValue, newBlockSelector, returnBlockSelector, hiddenClass, errorClass });
                hideCouponToast();
            }
            if (dropdownEl.id === 'state-dropdown' || dropdownEl.matches(patientDropdownId)) {
                if (dropdownEl.id === 'state-dropdown') state.stateCode = dropdownEl.dataset.value || null;
                updateRatesView(container).then(() => {
                    console.log('Rates updated after dropdown change');
                    initBookReload();
                });
            }
        },
        onReminderPick: (value) => {
            const hiddenInput = qs(container, '#appointmentReminder');
            const selected = dropdowns.find(d => d.el.id === 'reminder-dropdown')?.selected;
            if (hiddenInput) hiddenInput.value = value;
            if (selected) selected.classList.remove('placeholder');
            state.reminder = value || null;
        }
    });

    const stateDropdownEl = qs(container, '#state-dropdown');
    const patientDropdownEl = qs(container, '#patient-dropdown');

    function syncPatientDropdownAvailability() {
        const stateSelected = stateDropdownEl?.dataset.value;

        if (patientDropdownEl) {
            patientDropdownEl.classList.toggle('disabled', !stateSelected);
            patientDropdownEl.setAttribute('aria-disabled', !stateSelected);
        }
    }

    stateDropdownEl?.querySelectorAll('li').forEach(item => {
        item.addEventListener('click', () => setTimeout(syncPatientDropdownAvailability, 0));
    });

    syncPatientDropdownAvailability();

    initCouponToast();

    document.addEventListener('click', () => closeAllDropdowns(dropdowns));

    syncPatientDependentBlocks(container, { patientDropdownId, returningValue, newValue, newBlockSelector, returnBlockSelector, hiddenClass, errorClass });
    updateRatesView(container);

    // Initialize dynamic price loading
    //initDynamicPrices(container);

    //initFakeUploader(container, { errorClass, hiddenClass });
    initVeteranUploadToggle(container, { errorClass, hiddenClass });

    // Toggle veteran ID field visibility (based on old appointment.js)
    const veteranCheck = qs(container, '#veteranCheck') || qs(container, '#veteran');
    const veteranIdField = qs(container, '.veteran-id-field');

    const toggleVeteranId = () => {
        if (veteranIdField && veteranCheck) {
            veteranIdField.style.display = veteranCheck.checked ? 'block' : 'none';
        }
    };

    if (veteranCheck) {
        toggleVeteranId();
        syncCouponVisibility(container);
        veteranCheck.addEventListener('change', () => {
            state.isVeteran = !!veteranCheck.checked;
            toggleVeteranId();
            syncCouponVisibility(container);
            hideCouponToast();
        });
    }

    container.addEventListener('veteran-upload:changed', () => {
        state.hasVeteranFile = !!qs(container, CFG.selectors.uploadListItem);
    });

    returnInput?.addEventListener('input', () => { if (returnInput.value.trim()) returnInput.classList.remove(errorClass); });

    function initBookReload(){
        bindBookButtonsValidation(container, { buttonSelector, errorClass, hiddenClass, patientDropdownId, returningValue });
        bindClientFormValidation(container, { errorClass });

        initPaymentForm(container);
        initWorkflow(container, { hiddenClass, patientDropdownId, errorClass });

        const submitBtn = qs(container, '.book__payment .btn-submit');
        submitBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const payload = buildPayload(container);
            container.dispatchEvent(new CustomEvent('booking:submit', { detail: { payload } }));

            console.log('[booking:submit]', payload);
        });
    }

    initBookReload();

    // Listen for appointment loaded event to reinitialize after AJAX content load
    container.addEventListener('appointment:loaded', () => {
        console.log('Appointment content loaded, reinitializing...');
        initBookReload();
    });

    function initCouponToast () {
        if (document.documentElement.dataset.couponToastBound === 'true') {
            return;
        }

        const showCouponToast = (message = 'Coupon applied — $10 discount added to your order') => {
            const couponToast = document.getElementById('couponToast');
            const couponToastMessage = document.getElementById('couponToastMessage');

            if (!couponToast) {
                return;
            }

            if (couponToastMessage) {
                couponToastMessage.textContent = message;
            }

            couponToast.classList.add('is-visible');
            cleanupCouponModalState();
        };

        const closeCouponModal = () => {
            const couponModalEl = document.getElementById('coupon');

            if (!couponModalEl) {
                return;
            }

            const bootstrapModal = window.bootstrap?.Modal;

            if (bootstrapModal) {
                const couponModal = bootstrapModal.getOrCreateInstance(couponModalEl);
                couponModal.hide();
                requestAnimationFrame(() => {
                    setTimeout(cleanupCouponModalState, 350);
                });
                return;
            }

            couponModalEl.classList.remove('show');
            couponModalEl.setAttribute('aria-hidden', 'true');
            couponModalEl.style.display = 'none';
            cleanupCouponModalState();
        };

        const applyDiscountToDisplay = (discountType, discountValue) => {
            document.querySelectorAll('[data-price-default]').forEach((el) => {
                // find raw price from the Book Now button in the same row
                const row = el.closest('.book__pricesRow');
                if (!row) return;

                const bookBtn = row.querySelector('[data-service-price]');
                if (!bookBtn) return;

                const originalPrice = parseFloat(bookBtn.dataset.servicePrice);
                if (isNaN(originalPrice) || originalPrice <= 0) return;

                let newPrice;
                let saved;

                if (discountType === 'percent') {
                    saved     = (originalPrice * discountValue) / 100;
                    newPrice  = Math.max(0, originalPrice - saved);
                } else {
                    saved    = discountValue;
                    newPrice = Math.max(0, originalPrice - discountValue);
                }

                const formatted    = '$' + newPrice.toFixed(2);
                const oldFormatted = '$' + originalPrice.toFixed(2);

                // Replace or build the price HTML
                let priceNode = el.querySelector('.coupon-price');
                if (!priceNode) {
                    priceNode = document.createElement('span');
                    priceNode.className = 'coupon-price';
                }

                priceNode.innerHTML = `${formatted} <br><del class="old-price">${oldFormatted}</del>`;

                // Remove previous content and inject new
                el.innerHTML = '';
                el.appendChild(priceNode);

                // Update the button's price attribute so checkout uses the discounted price
                bookBtn.dataset.servicePrice = newPrice.toFixed(2);
                bookBtn.dataset.originalPrice = originalPrice.toFixed(2);
            });
        };

        document.addEventListener('click', (event) => {
            const closeBtn = event.target.closest('#couponToastClose');

            if (closeBtn) {
                hideCouponToast();
                return;
            }

            const applyBtn = event.target.closest('#applyCouponBtn, #coupon .modal-footer .btn-primary');

            if (!applyBtn) {
                return;
            }

            const codeInput   = document.getElementById('couponCodeInput');
            const modalBody   = document.querySelector('#coupon .modal-body');
            const errorEl     = modalBody?.querySelector('.error');
            const code        = codeInput?.value?.trim() ?? '';

            if (!code) {
                modalBody?.classList.add('is-invalid');
                if (errorEl) {
                    errorEl.textContent = 'Please enter a coupon code.';
                    errorEl.classList.remove('d-none');
                }
                return;
            }

            // Get currently selected state/location
            const stateDropdown = document.querySelector('#state-dropdown');
            const locationId    = stateDropdown?.dataset.value ?? null;

            // Get currently selected patient type
            const patientDropdown = document.querySelector('#patient-dropdown');
            const patientType     = patientDropdown?.dataset.value ?? '';

            applyBtn.disabled    = true;
            applyBtn.textContent = 'Applying…';

            // Collect the original service price(s) so the backend can record
            // price_before_discount accurately even before Book Now is clicked.
            const servicePriceBtns = document.querySelectorAll('.book-now-btn[data-service-price]');
            let originalServicePrice = null;
            servicePriceBtns.forEach(btn => {
                const val = parseFloat(btn.dataset.originalPrice ?? btn.dataset.servicePrice);
                if (!isNaN(val) && val > 0 && originalServicePrice === null) {
                    originalServicePrice = val;
                }
            });

            const csrf = getCsrf();
            fetch('/appointment/apply-coupon', {
                method:  'POST',
                headers: {
                    'Content-Type':    'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN':    csrf.hash,
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    code,
                    location_id: locationId,
                    patient_type: patientType,
                    price: originalServicePrice,
                    [csrf.name]: csrf.hash,
                }),
            })
                .then(res => {
                    // Refresh CSRF token from response header if present
                    const newHash = res.headers.get('X-CSRF-TOKEN');
                    const newName = res.headers.get('X-CSRF-TOKEN-NAME');
                    if (newHash) {
                        const meta = document.querySelector('meta[name="csrf_token"]');
                        if (meta) meta.setAttribute('content', newHash);
                    }
                    if (newName) {
                        const meta = document.querySelector('meta[name="csrf_token_name"]');
                        if (meta) meta.setAttribute('content', newName);
                    }
                    return res.json().then(data => ({ ok: res.ok, data }));
                })
                .then(({ ok, data }) => {
                    applyBtn.disabled    = false;
                    applyBtn.textContent = 'Apply Code';

                    if (!ok || !data.success) {
                        modalBody?.classList.add('is-invalid');
                        if (errorEl) {
                            errorEl.textContent = data.error || 'Invalid code. Try again.';
                            errorEl.classList.remove('d-none');
                        }
                        return;
                    }

                    // Success — clear error state
                    modalBody?.classList.remove('is-invalid');
                    if (errorEl) errorEl.classList.add('d-none');

                    // Apply discount to all displayed prices
                    applyDiscountToDisplay(data.discount_type, data.discount_value);

                    // Build toast message
                    const label = data.discount_type === 'percent'
                        ? `${data.discount_value}%`
                        : `$${parseFloat(data.discount_value).toFixed(2)}`;
                    const toastMsg = `Coupon applied — ${label} discount added to your order`;

                    closeCouponModal();
                    showCouponToast(toastMsg);

                    // Clear input for next time
                    if (codeInput) codeInput.value = '';
                })
                .catch(() => {
                    applyBtn.disabled    = false;
                    applyBtn.textContent = 'Apply Code';
                    modalBody?.classList.add('is-invalid');
                    if (errorEl) {
                        errorEl.textContent = 'Something went wrong. Please try again.';
                        errorEl.classList.remove('d-none');
                    }
                });
        });

        // on input couponCodeInput make uppercase and remove error state
        const codeInput = document.getElementById('couponCodeInput');
        codeInput?.addEventListener('input', () => {
            codeInput.value = codeInput.value.toUpperCase();
            const modalBody = document.querySelector('#coupon .modal-body');
            const errorEl = modalBody?.querySelector('.error');
            modalBody?.classList.remove('is-invalid');
            if (errorEl) errorEl.classList.add('d-none');
        });

        const couponModalEl = document.getElementById('coupon');

        couponModalEl?.addEventListener('hidden.bs.modal', cleanupCouponModalState);

        // Reset error state when modal is opened
        couponModalEl?.addEventListener('show.bs.modal', () => {
            const modalBody = couponModalEl.querySelector('.modal-body');
            const errorEl   = modalBody?.querySelector('.error');
            modalBody?.classList.remove('is-invalid');
            if (errorEl) errorEl.classList.add('d-none');
        });

        document.documentElement.dataset.couponToastBound = 'true';
    }

    function initVeteranUploadToggle (container, { errorClass = 'error', hiddenClass = 'd-none' } = {}) {
        const veteranCheck = qs(container, '#veteranCheck');
        const uploadBlock = qs(container, '.book__optionUpload');
        const veteranRow = qs(container, '.book__optionVeteran');
        const militaryIdInput = uploadBlock?.querySelector('input');
        const stateDropdown = qs(container, '#state-dropdown');
        const patientDropdown = qs(container, '#patient-dropdown');
        const militaryIdError = uploadBlock?.querySelector('.error-message');
        const militaryIdPattern = /^\d{8}[A-Za-z]{2}$/;

        function getDropdownValue (dropdown, placeholderText = '') {
            const datasetValue = dropdown?.dataset.value || '';
            if (datasetValue) return datasetValue;

            const selectedText = dropdown?.querySelector('[data-selected]')?.textContent?.trim() || '';
            if (selectedText && selectedText !== placeholderText) return selectedText;

            return '';
        }



        function syncUploadVisibility () {
            const stateVal = getDropdownValue(stateDropdown, 'Select your state');
            const patientVal = getDropdownValue(patientDropdown, 'Select patient type');
            const hasSelections = !!(stateVal && patientVal);
            const isNew = patientVal === 'new-patient';
            const isChecked = !!(veteranCheck && veteranCheck.checked && hasSelections && isNew);

            if (veteranRow) {
                veteranRow.classList.toggle(hiddenClass, !hasSelections);
            }

            if (veteranCheck) {
                veteranCheck.disabled = !hasSelections || !isNew;
                if (!hasSelections || !isNew) {
                    veteranCheck.checked = false;
                }
            }

            if (uploadBlock) {
                uploadBlock.classList.toggle(hiddenClass, !isNew || !hasSelections);
            }

            if (militaryIdInput) {
                militaryIdInput.disabled = !isChecked;
                if (!isChecked) {
                    militaryIdInput.value = '';
                    militaryIdInput.classList.remove(errorClass);
                }
            }

            if (!isChecked) {
                const list = qs(container, '#uploadList');
                const fileInput = qs(container, '#veteranFile');
                veteranRow?.classList.remove(errorClass);
                if (militaryIdError) militaryIdError.classList.add(hiddenClass);
                if (fileInput) fileInput.value = '';
                if (list) {
                    list.innerHTML = '';
                    list.classList.remove('has-active');
                }
            }

            updateRatesView(container).then(() => {
                console.log('Rates updated after checkbox change');
                initBookReload();
            });
        }

        if (militaryIdInput && !militaryIdInput.dataset.validationBound) {
            militaryIdInput.addEventListener('blur', () => validateMilitaryIdField(container, {
                errorClass,
                hiddenClass}));

            militaryIdInput.addEventListener('input', () => {
                let value = militaryIdInput.value.replace(/[^0-9a-zA-Z]/g, '');

                const digits = value.slice(0, 8).replace(/[^0-9]/g, '');
                const letters = value.slice(8).replace(/[^a-zA-Z]/g, '').slice(0, 2);

                militaryIdInput.value = digits + letters;

                militaryIdInput.classList.remove(errorClass);
                veteranRow?.classList.remove(errorClass);
                if (militaryIdError) militaryIdError.classList.add(hiddenClass);
            });

            // after 3 sec of no input, or on blur update rates if veteran ID field is visible and has value (assuming user finished entering ID)
            let debounceTimer;
            militaryIdInput.addEventListener('input', () => {
                clearTimeout(debounceTimer);
                if (militaryIdInput.value.trim()) {
                    debounceTimer = setTimeout(() => {
                        validateMilitaryIdField(container, {
                            errorClass,
                            hiddenClass});

                        if (!militaryIdInput.classList.contains(errorClass)) {
                            updateRatesView(container).then(() => {
                                console.log('----Rates updated after veteran ID input');
                                initBookReload();
                            });
                        }
                    }, 2000);
                }
            });


            militaryIdInput.dataset.validationBound = 'true';

        }

        veteranCheck?.addEventListener('change', syncUploadVisibility);
        stateDropdown?.querySelectorAll('li').forEach(item => {
            item.addEventListener('click', () => setTimeout(syncUploadVisibility, 0));
        });
        patientDropdown?.querySelectorAll('li').forEach(item => {
            item.addEventListener('click', () => setTimeout(syncUploadVisibility, 0));
        });

        syncUploadVisibility();
    }

}

export function validateMilitaryIdField (container, { errorClass = 'error', hiddenClass = 'd-none' } = {}) {
        const uploadBlock = qs(container, '.book__optionUpload');
        const veteranRow = qs(container, '.book__optionVeteran');
        const militaryIdInput = uploadBlock?.querySelector('input');
        const militaryIdError = uploadBlock?.querySelector('.error-message');
        const militaryIdPattern = /^\d{8}[A-Za-z]{2}$/;
            if (!militaryIdInput) return true;

            let value = militaryIdInput.value.trim();

            //if (value.length >= 2) {
                value = value.slice(0, -2) + value.slice(-2).toUpperCase();
                militaryIdInput.value = value;
            //}

            //const isEmpty = !value;
            const isEmpty = false; // Always validate if field is visible/enabled, even if empty - per client request
            const isValid = militaryIdPattern.test(value);
            const shouldValidate = !militaryIdInput.disabled && !isEmpty;

            militaryIdInput.classList.toggle(errorClass, shouldValidate && !isValid);
            veteranRow?.classList.toggle(errorClass, shouldValidate && !isValid);

            if (militaryIdError) {
                militaryIdError.classList.toggle(hiddenClass, !shouldValidate || isValid);
            }

            return isEmpty || isValid;
        }
